import React, { createContext, useContext, useMemo } from 'react'
import { useAuth } from './Auth'
import { useQuery } from '@apollo/client'
import { SUBSCRIPTION_STATE_QUERY } from '../queries/subscription'
import {
  BillingAccount,
  BillingAccountData,
  extractBillingAccount,
  extractSubscription,
  Subscription,
  SubscriptionQueryData
} from '../types/subscription'
import { isAfter } from 'date-fns'

interface ProviderValue {
  active?: Subscription
  future?: Subscription
  billingAccount: BillingAccount | null
  isExpiring: boolean
  completedLoading: boolean
}

export const SubscriptionContext = createContext<ProviderValue>({
  active: undefined,
  future: undefined,
  billingAccount: null,
  isExpiring: false,
  completedLoading: false,
})

type Props = {
  children?: React.ReactNode
}

interface SubscriptionStateData {
  subscriptionState: {
    active?: SubscriptionQueryData
    future?: SubscriptionQueryData
    billingAccount: BillingAccountData | null
  }
}

export const SubscriptionProvider: React.FC<Props> = ({ children }) => {
  const { isLoggedIn } = useAuth()
  // `skip` (rather than useLazyQuery) matters here: logout tears the query
  // down via clearStore, and Apollo 3.8's lazy execute promise crashes when
  // its in-flight request is torn down. The skip path unsubscribes cleanly.
  const { data, loading, error } = useQuery<SubscriptionStateData>(
    SUBSCRIPTION_STATE_QUERY,
    {
      fetchPolicy: 'no-cache',
      skip: !isLoggedIn,
    }
  )

  const value = useMemo(() => {
    if (!isLoggedIn) {
      return {
        active: undefined,
        future: undefined,
        billingAccount: null,
        isExpiring: false,
        completedLoading: false,
      }
    }
    const subscriptionState = data ? data.subscriptionState : undefined
    const active = (
      subscriptionState?.active ? extractSubscription(subscriptionState.active) : undefined
    )
    const future = (
      subscriptionState?.future ? extractSubscription(subscriptionState.future) : undefined
    )
    const billingAccount = (
      subscriptionState?.billingAccount
        ? extractBillingAccount(subscriptionState.billingAccount)
        : null
    )

    const completedLoading = !loading && (!!data || !!error)

    return {
      active,
      future,
      billingAccount,
      isExpiring: !!(active?.endAt && isAfter(new Date(active.endAt), new Date())),
      completedLoading,
    }
  }, [data, isLoggedIn, error, loading])

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscriptionState = (): ProviderValue => useContext(SubscriptionContext)
